import classNames from 'classnames';
import React, { useState } from 'react';
import IconButton from './IconButton';
import * as styles from './SideColumnLayout.module.scss';
import StickyHeader from './StickyHeader';
import SVGIcon from './SVGIcon';
import Tabs, { Tab } from './Tabs';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import settingsIcon from '../icons/non-prefilled/16x16/settings.svg';
import useEventListener from '../utils/useEventListener';

interface Column {
  children?: React.ReactNode;
  controls?: React.ReactNode;
  icon?: React.ReactNode;
  seamless?: boolean;
  title?: string;
}

interface SideColumnLayoutProps {
  aboveAll?: React.ReactNode;
  aboveMain?: React.ReactNode;
  main?: Column;
  sideColumn?: Column;
}

export function useIsCompactLayout() {
  const isSidebarOpen = useIsSidebarOpen();
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEventListener(window, 'resize', () => setWindowWidth(window.innerWidth), {
    options: { passive: true },
  });

  return windowWidth < (isSidebarOpen ? 1280 : 992);
}

const SideColumnLayout = ({
  aboveAll,
  aboveMain,
  main = {},
  sideColumn = {},
}: SideColumnLayoutProps) => {
  const [isMainTabActive, setIsMainTabActive] = useState(true);
  const [isShowingMobileControls, setIsShowingMobileControls] = useState(false);
  const isCompactLayout = useIsCompactLayout();

  if (isCompactLayout) {
    const activeView = isMainTabActive ? main : sideColumn;

    return (
      <div className={styles.compactLayoutContainer}>
        {!!aboveAll && (
          <div className={styles.aboveAllContainer}>{aboveAll}</div>
        )}
        {!!aboveMain && (
          <div className={styles.aboveColumnContainer}>{aboveMain}</div>
        )}
        <StickyHeader topbarAware>
          <div className={styles.tabsWithControls}>
            <div className={styles.tabs}>
              <Tabs>
                <Tab
                  onClick={() => setIsMainTabActive(true)}
                  active={isMainTabActive}
                >
                  {main.title}
                </Tab>
                <Tab
                  onClick={() => setIsMainTabActive(false)}
                  active={!isMainTabActive}
                >
                  {sideColumn.title}
                </Tab>
              </Tabs>
              {isShowingMobileControls && (
                <div className={styles.controls}>{activeView.controls}</div>
              )}
            </div>
            {!!activeView.controls && (
              <div
                className={classNames(styles.controlsToggle, {
                  [styles.active]: isShowingMobileControls,
                })}
              >
                <IconButton
                  onClick={() =>
                    setIsShowingMobileControls(!isShowingMobileControls)
                  }
                  applyNegativeMargin
                  aria-label="Settings"
                >
                  <SVGIcon>{settingsIcon}</SVGIcon>
                </IconButton>
              </div>
            )}
          </div>
        </StickyHeader>
        <div
          className={classNames(styles.activeViewContainer, {
            [styles.seamless]: activeView.seamless,
          })}
        >
          {activeView.children}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.layoutContainer)}>
      {!!aboveAll && <div className={styles.aboveAllContainer}>{aboveAll}</div>}
      <div className={styles.mainContainer}>
        {!!aboveMain && (
          <div className={styles.aboveColumnContainer}>{aboveMain}</div>
        )}
        <StickyHeader>
          <div className={styles.mainTitleContainer}>
            <h3 className={styles.mainTitle}>{main.title}</h3>
            <div>{main.controls}</div>
          </div>
        </StickyHeader>
        {main.children}
      </div>
      <div className={styles.sideColumnContainer}>
        {sideColumn && (
          <>
            <StickyHeader>
              <div className={styles.sideColumnTitleContainer}>
                {sideColumn.icon ? (
                  <>
                    <div className={styles.iconContainer}>
                      {sideColumn.icon}
                    </div>
                    <h4>{sideColumn.title}</h4>
                  </>
                ) : (
                  <h3>{sideColumn.title}</h3>
                )}
              </div>
            </StickyHeader>
            {sideColumn.children}
          </>
        )}
      </div>
    </div>
  );
};

export default SideColumnLayout;
